import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ga4Service from '../services/ga4Service';
import { httpClient } from '../services/httpClient';

// Base URL for API requests; falls back to window.location.origin when not set
const API_BASE_URL = import.meta.env['VITE_API_URL'] ?? '';

// Generate a cryptographically secure session ID for tracking
const generateSessionId = (): string => {
  const existing = sessionStorage.getItem('pageTrackingSessionId');
  if (existing) return existing;
  
  let sessionId: string;
  
  // Use crypto.randomUUID() if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    sessionId = crypto.randomUUID();
  } else if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    // Fallback: generate UUID v4 using crypto.getRandomValues
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    
    // Set version (4) and variant bits according to RFC 4122
    const byte6 = bytes[6] ?? 0;
    const byte8 = bytes[8] ?? 0;
    bytes[6] = (byte6 & 0x0f) | 0x40; // Version 4
    bytes[8] = (byte8 & 0x3f) | 0x80; // Variant 10
    
    // Convert to UUID format
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    sessionId = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
  } else {
    // Final fallback for very old browsers (should rarely be needed)
    sessionId = Date.now().toString(36) + Math.random().toString(36).substring(2);
  }
  
  sessionStorage.setItem('pageTrackingSessionId', sessionId);
  return sessionId;
};

// Batch processing queue
interface PageViewEvent {
  userId: string;
  path: string;
  referrer?: string | undefined;
  userAgent: string;
  sessionId: string;
  timestamp: Date;
}

class PageTrackingBatcher {
  private queue: PageViewEvent[] = [];
  private batchSize = 10;
  private flushInterval = 30000; // 30 seconds
  private lastFlush = Date.now();

  constructor() {
    // Set up periodic flushing
    setInterval(() => this.flushIfNeeded(), 5000); // Check every 5 seconds
    
    // Flush on page unload using synchronous method
    window.addEventListener('beforeunload', () => this.flushSync());
    window.addEventListener('pagehide', () => this.flushSync());
  }

  addEvent(event: PageViewEvent) {
    this.queue.push(event);
    
    // Flush if batch size reached
    if (this.queue.length >= this.batchSize) {
      this.flush();
    }
  }

  private flushIfNeeded() {
    const now = Date.now();
    
    // Flush if we have events and enough time has passed
    if (this.queue.length > 0 && (now - this.lastFlush) >= this.flushInterval) {
      this.flush();
    }
  }

  private async flush() {
    if (this.queue.length === 0) return;

    const events = [...this.queue];
    this.queue = [];
    this.lastFlush = Date.now();

    try {
      await httpClient.post('/api/analytics/track-pages-batch', { events });
    } catch (error) {
      // Silently fail - don't break the user experience
      // Could potentially re-queue events on failure, but keeping it simple
      if (import.meta.env.DEV || import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging') {
        console.debug('Page tracking batch failed:', error);
      }
    }
  }

  private flushSync(): void {
    // Handle empty queue early
    if (this.queue.length === 0) return;

    // Copy and clear the queue
    const events = [...this.queue];
    this.queue = [];
    this.lastFlush = Date.now();

    // Try to send via navigator.sendBeacon first
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      try {
        const payload = JSON.stringify({ events });
        const blob = new Blob([payload], { type: 'application/json' });

        // Construct the full URL for the batch endpoint using the same base
        // API URL as the rest of the app. Fall back to the current origin if
        // VITE_API_URL is not configured (e.g. local development).
        const trimmedApiBase =
          typeof API_BASE_URL === 'string' ? API_BASE_URL.trim() : '';
        const baseUrl =
          trimmedApiBase.length > 0 ? trimmedApiBase : window.location.origin;
        const url = `${baseUrl}/api/analytics/track-pages-batch`;
        
        const sent = navigator.sendBeacon(url, blob);
        
        // If sendBeacon succeeded, we're done
        if (sent) {
          return;
        }
      } catch {
        // Fall through to async flush fallback
      }
    }

    // Fallback: restore events to queue and call async flush
    this.queue = [...events, ...this.queue];
    this.flush().catch((error) => {
      // Silently fail - don't break the user experience
      if (import.meta.env.DEV || import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging') {
        console.debug('Page tracking sync fallback failed:', error);
      }
    });
  }
}

// Global batcher instance
const batcher = new PageTrackingBatcher();

export const usePageTracking = () => {
  const location = useLocation();
  const { user } = useAuth();
  const ga4InitializedRef = useRef(false);

  // Initialize GA4 when user changes
  useEffect(() => {
    if (user && !ga4InitializedRef.current) {
      ga4Service.initialize(user.email, user.role);
      ga4InitializedRef.current = true;
    }
  }, [user]);

  useEffect(() => {
    // Don't track if user is not logged in or is a contrarianthink.com user
    if (!user || !user.email || user.email.trim().toLowerCase().endsWith('@contrarianthink.com')) {
      return;
    }

    const trackPageView = () => {
      try {
        const sessionId = generateSessionId();

        // Existing internal page tracking
        const event: PageViewEvent = {
          userId: user._id,
          path: location.pathname,
          referrer: document.referrer || undefined,
          userAgent: navigator.userAgent,
          sessionId,
          timestamp: new Date()
        };

        // Add to batch queue instead of sending immediately
        batcher.addEvent(event);

        // GA4 page tracking
        const pageTitle = document.title || location.pathname;
        ga4Service.trackPageView(
          location.pathname,
          pageTitle,
          user.role,
          !!user.companyName
        );

      } catch {
        // Silently fail - don't break the user experience
      }
    };

    // Track the page view with a small delay to ensure page is fully loaded
    const timeoutId = setTimeout(trackPageView, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname, user]);
};

export default usePageTracking; 